import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import {
    AssetCondition,
    AssetTag,
    ClosedownJobDetail,
    ClosedownOption,
    ClosedownPayload,
    ClosedownStatus,
    ClosedownUploadRequest,
    EquipmentStatus,
    GasLeakTypeOption,
    RootCause,
    StatusAffirmation,
    TechnicianClosedownResponse
} from '@app/models/closedown-detail.model';
import { catchError, map, Observable, tap } from 'rxjs';
import { AllocatedJobDto, DashboardSummary, Job, JobAllocation, mapAllocatedJob, OpenJobsSummary, PickupJob, UnallocateJob } from 'src/app/models/job.model';
import { StorageKey } from 'src/app/shared/enums';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';

@Injectable({
    providedIn: 'root'
})
export class JobService extends BaseService {
    readonly KEY = StorageKey[StorageKey.JOB];
    readonly BASE_URL = `${environment.gatewayAPI_V1}/Jobs/`;
    //readonly BASE_URL = `https://localhost:57925/api/v1/`;

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getJobExistsByCompanyRefNumberNEmail(emailAddress: string, companyRefNumbers: string[]): Observable<string> {
        const url = `${this.BASE_URL}Jobs/getJobExistsByCompanyRefNumberNEmail`;
        let params = new HttpParams();

        params = params.append('userEmail', emailAddress);

        companyRefNumbers.forEach(refNumber => {
            params = params.append('companyRefNumbers', refNumber);
        })

        return this.httpClient.get<string>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsBySelectedReasons(reasons: string[], siteId: string, calloutReferenceNumbers: string[]): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsBySelectedReasons_v2`;
        let params = new HttpParams();

        reasons.forEach(refNumber => {
            params = params.append('reasons', refNumber);
        });

        calloutReferenceNumbers.forEach(crefNumber => {
            params = params.append('calloutReferenceNumbers', crefNumber);
        });

        params = params.append('siteId', siteId);
        
        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumberNEmail(companyRefNumbers: string[], siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumberNEmail`;
        let params = new HttpParams();

        companyRefNumbers.forEach(refNumber => {
            params = params.append('companyRefNumbers', refNumber);
        })

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumber(siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumber`;
        let params = new HttpParams();

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getResourceAdminJobsBySiteId(siteId: string): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetResourceAdminJobsBySiteId`;
        const params = new HttpParams().set('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getAllocatedJobById(jobId: string): Observable<ClosedownJobDetail | null> {
        const url = `${this.BASE_URL}Jobs/GetAllocatedJobById`;
        const params = new HttpParams().set('jobId', jobId);

        return this.httpClient.get<AllocatedJobDto | null>(url, { params }).pipe(
            map(job => job === null ? null : mapAllocatedJob(job)),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumberWithSkipHeader(siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumber`;
        let params = new HttpParams();

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        // Add skip header to hide loading spinner
        return this.httpClient.get<Job[]>(url, { params: params, headers:{skip:"true"} }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getDashboardSummary(): Observable<DashboardSummary> {
        const url = `${this.BASE_URL}Jobs/GetDashboardSummary`;

        return this.httpClient.get<DashboardSummary>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getOpenJobsSummary(): Observable<OpenJobsSummary[]> {
        const url = `${this.BASE_URL}Jobs/GetOpenJobsSummary`;

        return this.httpClient.get<OpenJobsSummary[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getAssetTag(siteId: string, assetTag: string): Observable<AssetTag> {
        const url = `${environment.gatewayAPI_V1}/jobs/Asset/GetByAssetTagAndSiteId`;
        const params = new HttpParams().set('siteId', siteId).set('assetTag', assetTag);

        return this.httpClient.get<AssetTag>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJob(jobId: string): Observable<ClosedownJobDetail> {
        const url = `${this.BASE_URL}Jobs/GetResourceAdminJobById`;
        const params = new HttpParams().set('jobId', jobId);

        return this.httpClient.get<ClosedownJobDetail>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getRootCauses(
        brandId: string,
        disciplineReferenceId: string,
        faultServiceReferenceId: string,
        incidentTypeReferenceId?: string
    ): Observable<RootCause[]> {
        const url = `${this.BASE_URL}RootCauses/GetAvailableRootCauses`;
        let params = new HttpParams()
            .set('brandId', brandId)
            .set('disciplineReferenceId', disciplineReferenceId)
            .set('faultServiceReferenceId', faultServiceReferenceId);

        if (incidentTypeReferenceId) {
            params = params.set('incidentTypeReferenceId', incidentTypeReferenceId);
        }

        return this.httpClient.get<RootCause[]>(url, { params }).pipe(
            map(rootCauses => [...(rootCauses ?? [])].sort(
                (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
            )),
            catchError(this.handleError)
        );
    }

    public getStatuses(): Observable<ClosedownStatus[]> {
        const url = `${this.BASE_URL}ClosedownStatuses/GetClosedownStatuses`;

        return this.httpClient.get<ClosedownStatus[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getEquipmentStatuses(): Observable<EquipmentStatus[]> {
        const url = `${this.BASE_URL}EquipmentStatuses/GetEquipmentStatuses`;

        return this.httpClient.get<EquipmentStatus[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getAssetConditions(): Observable<AssetCondition[]> {
        const url = `${this.BASE_URL}AssetConditions/GetAssetConditions`;

        return this.httpClient.get<AssetCondition[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getAffirmations(statusId: string): Observable<StatusAffirmation[]> {
        const url = `${this.BASE_URL}StatusAffirmations/GetStatusAffirmations`;
        const params = new HttpParams().set('closedownStatusId', statusId);

        return this.httpClient.get<StatusAffirmation[]>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getGasTypes(): Observable<ClosedownOption[]> {
        const url = `${this.BASE_URL}GasTypes/getGasTypes`;

        return this.httpClient.get<ClosedownOption[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getGasLeakTypes(): Observable<GasLeakTypeOption[]> {
        const url = `${this.BASE_URL}GasLeakTypes/GetGasLeakTypes`;

        return this.httpClient.get<GasLeakTypeOption[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public uploadFiles(request: ClosedownUploadRequest): Observable<void> {
        const url = `${this.BASE_URL}/FileStorages/UploadFiles`;

        return this.httpClient.post<void>(url, request).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public saveClosedown(payload: ClosedownPayload): Observable<ClosedownPayload> {
        const url = `${this.BASE_URL}Closedowns/Closedown`;

        return this.httpClient.post<ClosedownPayload>(url, payload).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public saveTechnicianClosedown(payload: ClosedownPayload): Observable<TechnicianClosedownResponse> {
        const url = `${this.BASE_URL}Closedowns/TechnicianClosedown`;

        return this.httpClient.post<TechnicianClosedownResponse>(url, payload).pipe(
            catchError(this.handleError)
        );
    }

    public getJobsByAllocatedEmail(allocatedEmail: string, siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsByAllocatedEmail`;
        let params = new HttpParams();

        params = params.append('allocatedEmail', allocatedEmail);

        if (siteId && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );

    }

    public getJobsByJobNSubContractorId(jobId: string, subContractorId: string): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsByJobNSubContractorId`;
        let params = new HttpParams();

        params = params.append('jobId', jobId);
        params = params.append('subContractorId', subContractorId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );

    }

    public pickupJob(pickupJob: PickupJob): Observable<boolean> {
        const url = `${this.BASE_URL}Jobs/PickupJob`;
        //const url = `https://localhost:7062/v1/gateway/Jobs/Jobs/PickupJob`;

        return this.httpClient.post<boolean>(url, pickupJob).pipe(
            tap(_ => _),
            catchError(this.handleError));
    }

    public jobAllocation(jobAllocation: JobAllocation): Observable<boolean> {
        const url = `${this.BASE_URL}Jobs/JobAllocation`;

        return this.httpClient.post<boolean>(url, jobAllocation).pipe(
            tap(_ => _),
            catchError(this.handleError));
    }

    public unallocateJob(unallocatedJob: UnallocateJob): Observable<boolean> {
        const url = `${this.BASE_URL}Jobs/UnallocateJob`;
        //const url = `https://localhost:7062/v1/gateway/Jobs/Jobs/UnallocateJob`;
        let params = new HttpParams();

        return this.httpClient.post<boolean>(url, unallocatedJob).pipe(
            tap(_ => _),
            catchError(this.handleError));
    }

    async getSignedInJob(): Promise<Job> {
        const item = await Preferences.get({ key: this.KEY });
        var obj;
        if (item.value != null)
            obj = JSON.parse(item.value);

        return obj;
    }

    async setSignedInJob(job: Job) {
        await Preferences.set({
            key: this.KEY,
            value: JSON.stringify(job),
        });
    }

    async removeSignedInJob() {
        await Preferences.remove({ key: StorageKey[this.KEY] });
    }
}